import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { ExaminerVoice } from '../../utils/examinerVoice';
import { examinerLaunchSettings, mergeExaminerTranscript } from '../../utils/examinerApi';
import { AircraftPicker, ExaminerError, SourceCitation, useExaminer } from './ExaminerShared';
import ExaminerAvatar from './ExaminerAvatar';
import ExaminerPreferences from './ExaminerPreferences';

const Transcript = ({ items }) => <div className="examiner-transcript" aria-label="Examination transcript">
  {items.map(item => <p key={item.event_key || item.eventKey}>
    <strong>{item.role === 'user' ? 'You' : 'Examiner'}</strong>
    {item.created_at && <> ({new Date(item.created_at).toLocaleString()})</>}
    {item.retained === false && ' (not retained)'}: {item.content ?? item.text}
  </p>)}
</div>;

const checkrides = [
  ['private', 'Private'],
  ['instrument', 'Instrument'],
  ['commercial', 'Commercial'],
  ['commercial_multi', 'Commercial Multi']
];

const ExaminerSession = () => {
  const { aircraft } = useApp();
  const { request, run, error, setError, busy, forbidden, active, onMissing } = useExaminer();
  const [config, setConfig] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [session, setSession] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [sources, setSources] = useState([]);
  const [searched, setSearched] = useState(false);
  const [query, setQuery] = useState('');
  const [state, setState] = useState('idle');
  const [notice, setNotice] = useState('');
  const [warning, setWarning] = useState('');
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [now, setNow] = useState(Date.now());
  const [deleting, setDeleting] = useState(false);
  const [toolResult, setToolResult] = useState(null);
  const [playbackLevel, setPlaybackLevel] = useState(0);
  const [activity, setActivity] = useState('idle');
  const [catalogStale, setCatalogStale] = useState(false);
  const [choosingExaminer, setChoosingExaminer] = useState(false);
  const [form, setForm] = useState({
    certificate: 'private', aircraftId: '', processingConsent: false, mockExamAcknowledged: false, recordingConsent: false
  });
  const voice = useRef(null);
  const current = useRef(null);
  const downloads = useRef(new Set());
  const setExam = data => { current.current = data; setSession(data); };
  const refreshHistory = async () => setHistory(await request('/sessions'));
  onMissing.current = async () => {
    const list = await request('/sessions');
    setHistory(list);
    if (config?.checkrideSelection?.version !== 1) setCatalog(await request('/catalog'));
    setCatalogStale(false);
    if (current.current && !list.some(item => item.id === current.current.id)) {
      voice.current?.stop(); setExam(null); setTranscript([]); setSources([]); setToolResult(null); setState('error');
    }
  };
  const load = () => run(async () => {
    setForm(previous => ({ ...previous, processingConsent: false, mockExamAcknowledged: false, recordingConsent: false }));
    const nextConfig = await request('/config');
    setConfig(nextConfig);
    setCatalog(nextConfig.checkrideSelection?.version === 1 ? [] : await request('/catalog'));
    setCatalogStale(false);
    await refreshHistory();
  });
  useEffect(() => {
    load();
    const urls = downloads.current;
    return () => { voice.current?.stop(); urls.forEach(url => URL.revokeObjectURL(url)); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return undefined;
    const timer = setInterval(() => {
      setNow(Date.now());
      if (Date.parse(current.current?.expiresAt) <= Date.now()) {
        voice.current?.stop();
        current.current = null;
        setSession(null); setTranscript([]); setSources([]); setState('error');
        setError(new Error('This examination has expired and its data is no longer available.'));
        setHistory(previous => previous.filter(item => Date.parse(item.expires_at) > Date.now()));
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [!!session, setError]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (error && voice.current) {
      voice.current.stop();
      setState('error');
    }
  }, [error]);

  const refreshDetail = async id => {
    try {
      const data = await request(`/sessions/${id}`);
      if (!active.current || current.current?.id !== id) return;
      setExam({ ...current.current, ...data });
      setTranscript(previous => mergeExaminerTranscript(previous, data.transcript || []));
    } catch (failure) {
      if (failure.status === 404) {
        voice.current?.stop(); setExam(null); setTranscript([]); setSources([]); setState('error');
      }
      throw failure;
    }
  };
  const stop = () => { voice.current?.stop(); voice.current = null; setMuted(false); setPlaybackLevel(0); setActivity('idle'); };
  const pause = () => { stop(); setState('paused'); setWarning('Paused. The original deadline continues running.'); };
  const connect = async (exam, pipeline) => {
    if (pipeline.closed || !active.current) return;
    setState('connecting');
    const ticket = await request(`/sessions/${exam.id}/voice-ticket`, { method: 'POST' });
    if (!active.current || pipeline.closed) return;
    pipeline.connect(ticket);
  };
  const makePipeline = () => {
    const pipeline = new ExaminerVoice(event => {
      if (!active.current || voice.current !== pipeline) return;
      if (event.type === 'examiner.ready') {
        setNow(Date.now());
        setState('active');
        setNotice(event.notice);
        setExam({ ...current.current, voiceDeadline: event.deadline, limits: event.limits, ...(event.profile ? { profile: event.profile } : {}) });
        setActivity('listening');
        setWarning('');
      } else if (event.type === 'examiner.transcript') {
        setTranscript(previous => mergeExaminerTranscript(previous, [event]));
      } else if (event.type === 'examiner.warning') setWarning(event.message);
      else if (event.type === 'input_audio_buffer.speech_started') { setActivity('listening'); setWarning('Listening to your answer...'); }
      else if (event.type === 'input_audio_buffer.speech_stopped') { setActivity('thinking'); setWarning('Answer received. Waiting for examiner...'); }
      else if (event.type === 'response.output_audio.done') setWarning('Examiner response generated; queued playback is finishing.');
      else if (event.type === 'examiner.tool_result') {
        if (event.name === 'retrieve_references' && Array.isArray(event.result)) { setSources(event.result); setSearched(true); }
        if (event.name === 'record_assessment') setToolResult(event.result);
        setWarning('Reference/assessment updated. Refresh the saved report to view persisted findings.');
        if (current.current?.id) run(() => refreshDetail(current.current.id));
      }
    }, failure => {
      if (!active.current || voice.current !== pipeline) return;
      setState('error'); setError(failure);
    }, level => {
      if (active.current && voice.current === pipeline) setPlaybackLevel(level);
    });
    voice.current = pipeline;
    return pipeline;
  };
  const start = event => {
    event.preventDefault();
    run(async () => {
      if (!config?.enabled) throw new Error('Mock examiner unavailable.');
      if (catalogStale) throw new Error('Checkride configuration changed. Reload configuration and history and provide fresh consent.');
      if (!checkrides.some(([value]) => value === form.certificate) || !aircraft?.some(item => Number(item.id) === Number(form.aircraftId))) throw new Error('Select a checkride and aircraft.');
      const settings = launchSettings;
      if (!settings) throw new Error('Automatic checkride preparation is not available for this aircraft on the current server. No microphone or examination was started.');
      if (!form.processingConsent || !form.mockExamAcknowledged || ((settings.retainAudio || settings.retainTranscript) && !form.recordingConsent)) throw new Error('Complete the separate consent acknowledgments before starting.');
      stop(); setState('preparing'); setTranscript([]); setSources([]); setWarning(''); setToolResult(null); setSearched(false);
      const pipeline = makePipeline();
      try {
        await pipeline.prepare();
        if (!active.current || pipeline.closed) return;
        pipeline.volume(volume);
        const { certificate, aircraftId, ...consent } = form;
        const selection = automaticSelection
          ? { certificate, aircraftId: Number(aircraftId) }
          : { curriculumId: selectedCurriculum.id, launchRevision: selectedCurriculum.launchRevision };
        const exam = await request('/sessions', { method: 'POST', body: { ...selection, ...consent, consentVersion: config.consentVersion } });
        if (!active.current) return;
        setExam(exam);
        setNotice(exam.notice);
        await refreshDetail(exam.id);
        await connect(exam, pipeline);
      } catch (failure) {
        pipeline.stop(); setState('error');
        if (failure.status === 409) {
          setCatalogStale(true);
          setForm(previous => ({ ...previous, processingConsent: false, mockExamAcknowledged: false, recordingConsent: false }));
        }
        if (failure.status === 404 && !automaticSelection) setCatalog(await request('/catalog'));
        throw failure;
      }
    });
  };
  const resume = () => run(async () => {
    if (!config?.enabled || !session || session.status === 'completed') throw new Error('This examination cannot open voice.');
    stop(); setState('preparing');
    const pipeline = makePipeline();
    try { await pipeline.prepare(); pipeline.volume(volume); if (active.current && !pipeline.closed) await connect(session, pipeline); }
    catch (failure) { pipeline.stop(); setState('error'); throw failure; }
  });
  const view = id => run(async () => {
    stop(); setState('paused'); setExam({ id }); setTranscript([]); setSources([]); setSearched(false); setDeleting(false); setToolResult(null);
    await refreshDetail(id);
    if (current.current?.status === 'completed') setState('completed');
  });
  const finish = () => run(async () => {
    stop(); setState('paused');
    const data = await request(`/sessions/${session.id}/complete`, { method: 'POST' });
    setExam(data); setTranscript(previous => mergeExaminerTranscript(previous, data.transcript || [])); setState('completed');
    await refreshHistory();
  });
  const remove = () => run(async () => {
    stop();
    const id = session.id;
    try {
      await request(`/sessions/${id}`, { method: 'DELETE' });
    } catch (failure) {
      if (failure.status !== 404) throw failure;
    }
    setExam(null); setTranscript([]); setSources([]); setNotice(''); setWarning(''); setDeleting(false); setState('idle'); setToolResult(null);
    downloads.current.forEach(url => URL.revokeObjectURL(url)); downloads.current.clear();
    await refreshHistory();
  });
  const download = direction => run(async () => {
    const blob = await request(`/sessions/${session.id}/audio/${direction}`, { binary: true });
    if (!active.current) return;
    const url = URL.createObjectURL(blob);
    downloads.current.add(url);
    const anchor = document.createElement('a');
    anchor.href = url; anchor.download = `mock-exam-${direction}.wav`; anchor.click();
    setTimeout(() => { URL.revokeObjectURL(url); downloads.current.delete(url); }, 1000);
  });
  const automaticSelection = config?.checkrideSelection?.version === 1;
  const matches = catalog.filter(item => item.certificate === form.certificate && Number(item.launchSettings?.aircraftId) === Number(form.aircraftId));
  const selectedCurriculum = matches.length === 1 ? matches[0] : null;
  const automaticSettings = config?.checkrideSelection;
  const launchSettings = automaticSelection
    ? typeof automaticSettings.retainTranscript === 'boolean' && typeof automaticSettings.retainAudio === 'boolean' ? automaticSettings : null
    : examinerLaunchSettings(selectedCurriculum);
  const change = (key, value) => setForm(previous => ['certificate', 'aircraftId'].includes(key)
    ? { ...previous, [key]: value, processingConsent: false, mockExamAcknowledged: false, recordingConsent: false }
    : { ...previous, [key]: value });
  const connected = ['preparing', 'connecting', 'active'].includes(state);
  const covered = new Set((session?.coverage || []).map(item => item.task_code));
  const total = session?.tasks?.length || 0;
  const remaining = session?.voiceDeadline ? Math.max(0, Math.ceil((Date.parse(session.voiceDeadline) - now) / 1000)) : null;
  return <section>
    <h2>AI Mock Oral Examiner</h2>
    <p>This is mock training, not an FAA DPE, official practical test, endorsement, or flight proficiency evaluation. AI assessments are provisional and not instructor-reviewed.</p>
    {config && <p className="examiner-notice">{config.notice}</p>}
    <ExaminerError error={error} />
    {catalogStale && <p role="status">Checkride configuration changed or is unavailable. Reload configuration and history, then provide fresh consent. The examination was not retried automatically.</p>}
    <button disabled={busy || connected} onClick={load}>Reload configuration and history</button>
    {!config && <p>Load examiner configuration to begin.</p>}
    {config && !config.enabled && <p role="status">Mock examiner unavailable. Microphone and exam creation are disabled.</p>}
    {config && !session && <>
      <details onToggle={event => setChoosingExaminer(event.currentTarget.open)}>
        <summary>Choose examiner appearance and voice (optional)</summary>
        <p>This saves your personal preference. The server currently determines the examination profile; your examination displays the actual assigned examiner.</p>
        {choosingExaminer && <ExaminerPreferences />}
      </details>
      <form onSubmit={start}>
        <label>Checkride
          <select required value={form.certificate} onChange={event => change('certificate', event.target.value)}>
            <option value="">Select checkride</option>
            {checkrides.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
        <AircraftPicker aircraft={aircraft} value={form.aircraftId} onChange={value => change('aircraftId', value)} />
        {form.certificate && form.aircraftId && !launchSettings && <p role="status">Automatic checkride preparation is not available for this aircraft on the current server. No setup is required from you; the server must support this checkride and aircraft combination.</p>}
        {launchSettings && <p>Transcript storage: {launchSettings.retainTranscript ? 'on' : 'off'}; audio storage: {launchSettings.retainAudio ? 'on' : 'off'}.</p>}
        {selectedCurriculum?.setupGroup && <p role="status">Part {selectedCurriculum.setupGroup.part} of {selectedCurriculum.setupGroup.totalParts} linked ACS curricula. Each part is a separate examination with its own usage reservation. Completing this part does not complete the whole ACS.</p>}
        {[
          ['processingConsent', 'I consent to microphone audio processing through OpenAI.'],
          ['mockExamAcknowledged', 'I understand this is AI mock training, not an official checkride.'],
          ...((launchSettings?.retainTranscript || launchSettings?.retainAudio)
            ? [['recordingConsent', `I explicitly consent to recording/storage of ${[launchSettings.retainTranscript && 'my transcript', launchSettings.retainAudio && 'my microphone and generated examiner audio'].filter(Boolean).join(' and ')} for this checkride.`]] : [])
        ].map(([key, label]) => <label className="examiner-check" key={key}><input type="checkbox" checked={form[key]} onChange={event => change(key, event.target.checked)} />{label}</label>)}
        <p>Stored exams are retained for {config.retentionDays} days, with early deletion/withdrawal available below. Metadata and generic coverage remain for practice without retention. Provider processing/retention is governed separately; application deletion does not erase provider records or pre-existing backups.</p>
        <button type="submit" disabled={busy || connected || !config.enabled || !form.certificate || !form.aircraftId || !launchSettings || catalogStale || forbidden.includes('/sessions')}>Start mock oral examination</button>
        {state === 'preparing' && <button type="button" onClick={pause}>Cancel microphone preparation</button>}
      </form>
    </>}
    {session && <div>
      <h3>Examination — {session.certificate} / {session.mode}</h3>
      <ExaminerAvatar profile={session.profile} level={state === 'active' ? playbackLevel : 0} activity={state === 'active' ? activity : state} />
      {session.profile && <p>Examiner: {session.profile.name}; voice: {session.profile.voiceId}. Appearance and voice are pinned to this examination.</p>}
      <p>Fictional animated character. Mouth movement follows audible playback, not exact phoneme-level lip synchronization.</p>
      <p role="status">Voice: {state}. {muted ? 'Microphone muted.' : state === 'active' ? 'Microphone active.' : 'Microphone off.'}</p>
      <p>{notice || session.notice}</p>
      <p>Pinned allowance: {session.limits?.sessionMinutes} minutes; {session.limits?.responsesPerSession} responses; {session.limits?.toolCallsPerSession} reference/assessment calls.</p>
      {remaining !== null && <p>Time remaining: {Math.floor(remaining / 60)}:{String(remaining % 60).padStart(2, '0')} — deadline {new Date(session.voiceDeadline).toLocaleString()}. Pausing/reconnecting does not extend it.</p>}
      {warning && <p role="status">{warning}</p>}
      <div className="examiner-actions">
        {state === 'active' && <>
          <button onClick={() => { voice.current?.mute(!muted); setMuted(!muted); }}>{muted ? 'Unmute microphone' : 'Mute microphone'}</button>
          <button onClick={() => { voice.current?.cancel(); setPlaybackLevel(0); setActivity('listening'); }}>Interrupt examiner</button>
          <label>Playback volume<input type="range" min="0" max="1" step="0.05" value={volume} onChange={event => { const value = Number(event.target.value); setVolume(value); voice.current?.volume(value); }} /></label>
        </>}
        {connected && <button onClick={pause}>Pause voice</button>}
        {!connected && session.status !== 'completed' && <button disabled={busy || !config?.enabled || remaining === 0 || forbidden.some(path => path.includes('voice-ticket'))} onClick={resume}>Connect / reconnect voice</button>}
        {session.status !== 'completed' && !forbidden.includes(`/sessions/${session.id}/complete`) && <button disabled={busy} onClick={finish}>Finish examination</button>}
        <button disabled={busy} onClick={() => run(() => refreshDetail(session.id))}>Refresh saved report</button>
        {!connected && <button disabled={busy} onClick={() => { setExam(null); setTranscript([]); setSources([]); setToolResult(null); setState('idle'); setForm(previous => ({ ...previous, processingConsent: false, mockExamAcknowledged: false, recordingConsent: false })); }}>Back to preparation</button>}
      </div>
      <p>Reconnect uses a new ticket for this same exam. A crashed connection may need up to 30 seconds to release its lease. Do not create another exam to bypass the wait.</p>
      <h3>Tracked oral elements discussed/assessed</h3>
      <p>{covered.size}/{total} ({total ? Math.round(covered.size / total * 100) : 0}%) of this curriculum, not the full FAA ACS. Instructor reviewed: false. No official pass/fail.</p>
      {toolResult && <div><strong>Provisional AI mock finding — not instructor-reviewed</strong><pre>{JSON.stringify(toolResult, null, 2)}</pre></div>}
      <ul>{(session.tasks || []).map(task => {
        const result = session.coverage?.find(item => item.task_code === task.code);
        return <li key={task.code}>{task.code} — {task.title}: {result ? session.mode === 'practice' ? 'discussed' : `provisional ${result.assessment?.result || 'assessment'}` : 'not covered'}
          {result && <pre>{JSON.stringify(result.assessment, null, 2)}</pre>}
        </li>;
      })}</ul>
      {!!session.uncovered?.length && <p>Uncovered: {session.uncovered.join(', ')}</p>}
      <Transcript items={transcript} />
      {session.consent && <p>Transcript retention: {session.consent.retainTranscript ? 'yes' : 'no'}; audio retention: {session.consent.retainAudio ? 'yes' : 'no'}; accepted {session.consent.acceptedAt}.</p>}
      <form onSubmit={event => { event.preventDefault(); run(async () => { setSources(await request(`/sessions/${session.id}/references/search`, { method: 'POST', body: { query } })); setSearched(true); }); }}>
        <label>Search this exam's pinned sources<input required maxLength={300} value={query} onChange={event => setQuery(event.target.value)} /></label>
        {!forbidden.includes(`/sessions/${session.id}/references/search`) && <button disabled={busy}>Search references</button>}
      </form>
      {searched && !sources.length && <p>No matching approved sources. No substitute citations are available.</p>}
      {sources.map(source => <SourceCitation source={source} key={source.id} />)}
      <p>Recording tracks contain captured/generated chunks, not synchronized audio of exactly what was heard or the silence between utterances. A missing track can mean storage was declined or nothing was flushed.</p>
      {session.consent?.retainAudio && <div className="examiner-actions">
        {['user', 'assistant'].map(direction => !forbidden.includes(`/sessions/${session.id}/audio/${direction}`) && <button key={direction} disabled={busy} onClick={() => download(direction)}>Download {direction} WAV</button>)}
      </div>}
      {!deleting ? !forbidden.includes(`/sessions/${session.id}`) && <button disabled={busy} onClick={() => { stop(); setState('paused'); setDeleting(true); }}>Delete exam / withdraw recording consent</button> : <div role="alert">
        <p>Delete this examination, transcript, audio and assessments? Usage is not refunded. Provider retention and backups are governed separately.</p>
        <button disabled={busy} onClick={remove}>Confirm permanent deletion</button>
        <button disabled={busy} onClick={() => setDeleting(false)}>Keep examination</button>
      </div>}
    </div>}
    <h3>Your recent examinations</h3>
    <ul>{history.map(item => <li key={item.id}>{item.certificate} / {item.mode} — {item.status} — {new Date(item.created_at).toLocaleString()}
      <button disabled={busy || connected} onClick={() => view(item.id)}>View / resume</button>
    </li>)}</ul>
    {!history.length && <p>No retained examination sessions.</p>}
  </section>;
};
export default ExaminerSession;
